import OpenSeadragon from 'openseadragon'

const OSDanimations = {
  params: {
    splineFrames: 500,
    maxFps: 10
  },
  easings: {
    /**
     *
     * @param x number
     * @return number
     */
    easeOutSine: function (x) {
      return Math.sin((x * Math.PI) / 2)
    },
    /**
     *
     * @param (number} x
     * @return {number}
     */
    easeInOutSine: function (x) {
      return -(Math.cos(Math.PI * x) - 1) / 2
    },
    /**
     * @param {number} x
     * @return {number}
     */
    easeOutCubic: function (x) {
      return 1 - Math.pow(1 - x, 3)
    },
    /**
     *
     * @param x {number}
     * @return {number}
     */
    easeInOutCubic: function (x) {
      return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2
    },

    /**
     *
     * @param x
     */
    easeOutLinear: function (x) {
      return x
    },

    /**
     *
     * @param x number
     * @return {number}
     */
    easeOutBounce: function (x) {
      const n1 = 7.5625
      const d1 = 2.75

      if (x < 1 / d1) {
        return n1 * x * x
      } else if (x < 2 / d1) {
        return n1 * (x -= 1.5 / d1) * x + 0.75
      } else if (x < 2.5 / d1) {
        return n1 * (x -= 2.25 / d1) * x + 0.9375
      } else {
        return n1 * (x -= 2.625 / d1) * x + 0.984375
      }
    },
    /**
     *
     * @param x number
     * @return {number}
     */
    easeInOutBounce: function (x) {
      return x < 0.5 ? (1 - easeOutBounce(1 - 2 * x)) / 2 : (1 + easeOutBounce(2 * x - 1)) / 2
    }
  },
  animationFrame: {
    finishRect: null,
    animationMs: 0
  },
  /**
   *
   * @param {OpenSeadragon.Viewer} viewer
   * @param {OpenSeadragon.Rect} targetRect
   * @param {number} totalAnimationMS
   * @param {function} panEasingFunction
   * @param {function} zoomEasingFunction
   */
  animateMovement(
    viewer,
    targetRect,
    totalAnimationMS,
    panEasingFunction = this.easings.easeOutCubic,
    zoomEasingFunction = this.easings.easeOutCubic
  ) {
    let stepAnimations = OSDanimations.calculateSplineAnimations(
      viewer.viewport.getBounds(),
      targetRect,
      totalAnimationMS,
      panEasingFunction,
      zoomEasingFunction,
      viewer
    )
    OSDanimations.animateSplineFrames(viewer, stepAnimations)
  },
  /**
   * @private
   *
   * @param {OpenSeadragon.Viewer} viewer
   * @param {OSDanimations.animationFrame[]} animationFrames
   * @param {number} frameIndex
   */
  animateSplineFrames(viewer, animationFrames, frameIndex = 0) {
    let frame = animationFrames[frameIndex]
    let finishRect = frame.finishRect
    let milliseconds = frame.animationMs

    let splineTimeout = setTimeout(
      function () {
        clearTimeout(splineTimeout)
        OSDanimations.setAnimationParameters(viewer.viewport, milliseconds)

        let animationFinished = frameIndex >= animationFrames.length - 1
        if (!animationFinished) {
          OSDanimations.animateSplineFrames(viewer, animationFrames, frameIndex + 1)
        } else {
          let distance = viewer.viewport.getCenter().distanceTo(finishRect.getCenter())
          if (distance < 0.005) {
            viewer.raiseEvent('animation-easing-finish', {})
          } else {
            viewer.addOnceHandler('animation-finish', function () {
              viewer.raiseEvent('animation-easing-finish', {})
            })
          }
          OSDanimations.resetAnimationParameters(viewer.viewport)
        }

        viewer.viewport.fitBounds(finishRect, false)
      },
      frameIndex === 0 ? 0 : milliseconds
    )
  },
  /**
   * @private
   *
   * @param {OpenSeadragon.Viewer} viewer
   * @param {number} animationTimeMs
   */
  setAnimationParameters: function (viewport, animationTimeMs) {
    const stiffness = 0.00001
    let animationTime = animationTimeMs / 1000
    let exponential = false

    viewport.centerSpringX.springStiffness = stiffness
    viewport.centerSpringX.animationTime = animationTime

    viewport.centerSpringY.springStiffness = stiffness
    viewport.centerSpringY.animationTime = animationTime

    viewport.zoomSpring.springStiffness = stiffness
    viewport.zoomSpring.animationTime = animationTime
    viewport.zoomSpring.exponential = exponential
  },

  resetAnimationParameters: function (viewport) {
    const stiffness = 6.5
    let exponential = false

    viewport.centerSpringX.springStiffness = stiffness

    viewport.centerSpringY.springStiffness = stiffness

    viewport.zoomSpring.springStiffness = stiffness
    viewport.zoomSpring.exponential = exponential
  },
  /**
   * @private
   * Creates spline animation frames. If distance berween start and finish is < 20% returns just one step
   *
   * @param {OpenSeadragon.Rect} startRect
   * @param {OpenSeadragon.Rect} finishRect
   * @param {number} totalAnimationTimeMS
   * @param {function} panEasingFunction
   * @param {function} zoomEasingFunction
   * @param {OpenSeadragon.Viewer} viewer
   * @return {OSDanimations.animationFrame[]}
   */
  calculateSplineAnimations: function (
    startRect,
    finishRect,
    totalAnimationTimeMS,
    panEasingFunction,
    zoomEasingFunction,
    viewer
  ) {
    //if moving too little do a single frame animation
    let distance = startRect.getCenter().distanceTo(finishRect.getCenter())
    if (distance < 0.05) {
      let result = []
      result[0] = {
        finishRect: finishRect,
        animationMs: totalAnimationTimeMS
      }
      return result
    }

    //limit number of frames to maxFPS
    let numberOfFrames = OSDanimations.params.splineFrames
    let msPerFrame = totalAnimationTimeMS / numberOfFrames

    while (msPerFrame < 1000 / OSDanimations.params.maxFps) {
      numberOfFrames--
      msPerFrame = totalAnimationTimeMS / numberOfFrames
    }

    //calculate spline factors
    let splineFramePositionFactors = []
    for (let x = 1; x <= numberOfFrames; x++) {
      //calculate spline factor for this frame
      splineFramePositionFactors[x - 1] = panEasingFunction(x / numberOfFrames)
    }

    //Find out if traveling to a long distance
    let flightDistanceImagePixels = viewer.viewport.viewportToImageCoordinates(
      finishRect.getCenter().minus(startRect.getCenter())
    )
    let currentVisibleImagePixels = viewer.viewport.viewportToImageCoordinates(
      viewer.viewport.getBounds().getTopRight().minus(viewer.viewport.getBounds().getBottomLeft())
    )

    let flightDistanceScreens = Math.max(
      Math.abs(flightDistanceImagePixels.x / currentVisibleImagePixels.x),
      Math.abs(flightDistanceImagePixels.y / currentVisibleImagePixels.y)
    )

    let longDistance = flightDistanceScreens > 0.8

    //calculate mid point rect dimension (zoom)
    let centerDistance = Math.abs(finishRect.getCenter().distanceTo(startRect.getCenter()))
    let midflightFrame = Math.round(numberOfFrames / 2)
    let midflightRectSize = new OpenSeadragon.Point(finishRect.width, finishRect.height)
      .plus(new OpenSeadragon.Point(startRect.width, startRect.height))
      .divide(2)
    if (longDistance) {
      let biggestRect = new OpenSeadragon.Rect(finishRect.x, finishRect.y, finishRect.width, finishRect.height)
      biggestRect.width = Math.max(finishRect.width, startRect.width)
      biggestRect.height = Math.max(finishRect.height, startRect.height)
      let maxRect = biggestRect.times(1 + OSDanimations.easings.easeInOutSine(centerDistance))
      midflightRectSize = new OpenSeadragon.Point(maxRect.width, maxRect.height)
    }

    //Calculate frames sizes based on movement type, long/short distance and take-off/landing animations
    let framesWidths = []
    let framesHeights = []

    let startToFinishWidthDiff = finishRect.width - startRect.width
    let startToFinishHeightDiff = finishRect.height - startRect.height

    let takeoffToMidflightWidthDiff = Math.abs(startRect.width - midflightRectSize.x)
    let takeoffToMidflightHeightDiff = Math.abs(startRect.height - midflightRectSize.y)
    for (let x = 1; x <= midflightFrame; x++) {
      let splineFactor = 0

      if (longDistance) {
        //On long distance movement there will be a zoom out at midflight
        splineFactor = zoomEasingFunction(x / midflightFrame)
        framesWidths[x - 1] = startRect.width + takeoffToMidflightWidthDiff * splineFactor
        framesHeights[x - 1] = startRect.height + takeoffToMidflightHeightDiff * splineFactor
      } else {
        //On short distance movement the flight will be linear
        splineFactor = zoomEasingFunction(x / numberOfFrames)

        framesWidths[x - 1] = startRect.width + startToFinishWidthDiff * splineFactor
        framesHeights[x - 1] = startRect.height + startToFinishHeightDiff * splineFactor
      }
    }
    let midflightToLandingWidthDiff = Math.abs(finishRect.width - midflightRectSize.x)
    let midflightToLandingHeightDiff = Math.abs(finishRect.height - midflightRectSize.y)
    for (let x = 0; x < numberOfFrames - midflightFrame; x++) {
      let splineFactor = 0

      if (longDistance) {
        splineFactor = zoomEasingFunction((numberOfFrames - midflightFrame - 1 - x) / (numberOfFrames - midflightFrame))
        framesWidths[x + midflightFrame] = finishRect.width + midflightToLandingWidthDiff * splineFactor
        framesHeights[x + midflightFrame] = finishRect.height + midflightToLandingHeightDiff * splineFactor
      } else {
        splineFactor = zoomEasingFunction((x + midflightFrame + 1) / numberOfFrames)
        framesWidths[x + midflightFrame] = startRect.width + startToFinishWidthDiff * splineFactor
        framesHeights[x + midflightFrame] = startRect.height + startToFinishHeightDiff * splineFactor
      }
    }

    //Create the final animation frames
    let splineAnimations = []
    let pathDistance = finishRect.getCenter().minus(startRect.getCenter())
    for (let splineFrame = 0; splineFrame < numberOfFrames; splineFrame++) {
      //next center position depends on spline factors
      let stepFinishCenter = pathDistance.times(splineFramePositionFactors[splineFrame]).plus(startRect.getCenter())

      let frameFinishWidth = framesWidths[splineFrame]
      let frameFinishHeight = framesHeights[splineFrame]

      let frameFinishRect = new OpenSeadragon.Rect(
        stepFinishCenter.x - frameFinishWidth / 2,
        stepFinishCenter.y - frameFinishHeight / 2,
        frameFinishWidth,
        frameFinishHeight
      )
      splineAnimations[splineFrame] = {
        finishRect: frameFinishRect,
        animationMs: msPerFrame
      }
    }

    //last animation is the exact finish rect
    splineAnimations[numberOfFrames - 1] = {
      finishRect: finishRect,
      animationMs: msPerFrame
    }
    return splineAnimations
  }
}

export default OSDanimations
